import { apiGet } from "./api";

/** The envelope every limit/offset list endpoint in the portal API returns. */
interface ListPage<T> {
  results: T[];
  total_count: number;
  has_more: boolean;
}

/**
 * Load every page of a limit/offset list endpoint.
 *
 * The portal tables sort, select and filter in the browser, so they need the
 * whole list, not the first page. Returns the first page's envelope (its
 * totals and aggregates already cover every matching row) with the rows of
 * all pages merged in.
 *
 * Rows are merged by id, because the endpoints order by a field that can tie
 * (receipts by date), so a row can show up on two pages. `maxRows` keeps a
 * very large list from freezing the page; when `results.length` ends up below
 * `total_count`, the caller says so instead of hiding rows silently.
 */
export async function apiGetAllPages<
  T extends { id: string },
  R extends ListPage<T>,
>(
  buildPath: (limit: number, offset: number) => string,
  { pageSize, maxRows }: { pageSize: number; maxRows: number },
): Promise<R> {
  const first = await apiGet<R>(buildPath(pageSize, 0));
  const byId = new Map<string, T>();
  for (const row of first.results) byId.set(row.id, row);

  let offset = first.results.length;
  let hasMore = first.has_more;
  while (hasMore && byId.size < maxRows) {
    const page = await apiGet<R>(buildPath(pageSize, offset));
    if (page.results.length === 0) break;
    for (const row of page.results) byId.set(row.id, row);
    offset += page.results.length;
    hasMore = page.has_more;
  }

  return { ...first, results: Array.from(byId.values()).slice(0, maxRows) };
}
